/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ReactNode, useEffect } from "react";
import type { AppProps } from "next/app";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import { CookiesProvider } from "react-cookie";
import { ApolloProvider } from "@apollo/client";
import UserProvider from "@/providers/UserProvider";
import { ErrorProvider } from "@/providers/ErrorProvider";
import ErrorBoundary from "@/components/ErrorBoundary";
import { createApolloClient } from "@/utils/apolloClient";
import { handleImpersonationTokenFromUrl } from "@/utils/impersonation";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { parseError } from "@/types/errors";
import {
  colors,
  components,
  fonts,
  textStyles,
  layerStyles,
  breakpoints,
} from "@/theme";

const defaultTheme = {
  colors,
  components,
  fonts,
  textStyles,
  layerStyles,
  breakpoints,
};

const theme = extendTheme(defaultTheme);
const apolloClient = createApolloClient();

export default function App({ Component, pageProps }: AppProps) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const getLayout = Component.getLayout || ((page: ReactNode) => page);

  // Handle impersonation token from URL on mount
  useEffect(() => {
    handleImpersonationTokenFromUrl();
  }, []);

  // Global error handlers for uncaught errors
  useEffect(() => {
    // Handle uncaught JavaScript errors
    const handleError = (event: ErrorEvent) => {
      event.preventDefault();
      parseError(event.error || new Error(event.message));

      if (process.env.NODE_ENV === "development") {
        console.error("Uncaught error:", event.error);
      }

      // Log to Sentry in production
      if (process.env.NODE_ENV === "production") {
        // @ts-ignore
        if (window.Sentry) {
          // @ts-ignore
          window.Sentry.captureException(event.error || event.message, {
            contexts: {
              errorEvent: {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
              },
            },
          });
        }
      }
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      event.preventDefault();
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      parseError(error);

      if (process.env.NODE_ENV === "development") {
        console.error("Unhandled promise rejection:", event.reason);
      }

      // Log to Sentry in production
      if (process.env.NODE_ENV === "production") {
        // @ts-ignore
        if (window.Sentry) {
          // @ts-ignore
          window.Sentry.captureException(event.reason, {
            contexts: {
              promiseRejection: {
                type: "unhandledrejection",
              },
            },
          });
        }
      }
    };

    // Add event listeners
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    // Cleanup
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  return (
    <CookiesProvider>
      <ApolloProvider client={apolloClient}>
        <ChakraProvider theme={theme}>
          <ErrorProvider>
            <ErrorBoundary>
              <ImpersonationBanner />
              <UserProvider>
                {getLayout(<Component {...pageProps} />)}
              </UserProvider>
            </ErrorBoundary>
          </ErrorProvider>
        </ChakraProvider>
      </ApolloProvider>
    </CookiesProvider>
  );
}
